// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { AlertUtils } from '@shared/utils/Alert/AlertUtils';
import { AlertAction, AlertType, AlertImportance } from '@shared/utils/Alert/AlertConstants';
import { AlertSendToServerItem } from '@shared/commons/cache/Alert';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
import CreateAlertTemplate from '@front/templates/CreateAlertTemplate.html';
import CreateAlertFooterTemplate from '@front/templates/CreateAlertFooterTemplate.html';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { AdvancedOrderEntry } from './AdvancedOrderEntry';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { DataCache } from '@shared/commons/DataCache';
import { PriceLimitValidation } from '@shared/utils/Instruments/PriceLimitValidation';
import { ApplicationPanel } from './ApplicationPanel';
import { DateSelectorButton } from '../elements/Lookup/TerceraDateSelectorDropDown';
import { WDSettings } from '../settings/WDGeneralSettings';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { AccountWidgetAccountsController } from '@shared/commons/AccountWidget/AccountWidgetAccountsController';
export class CreateAlertPanel extends ApplicationPanel {
    constructor() {
        super(...arguments);
        this.headerLocaleKey = 'screen.Alerts.Header.Create';
        this.myAlert = null;
        this.NeedCalculateRowCount = false;
        this.skipOnSave = true;
        this.MyTimeLookupOpened = false;
        this.afterSetFocus = null;
    }
    getType() {
        return PanelNames.CreateAlertPanel;
    }
    oninit() {
        super.oninit();
        this.on('btnCreateOrApplyClick', this.onCreateOrApplyClick);
        this.on('btnCancelClick', this.onCancelClick);
        this.on('showOrderEntryEvent', this.showOrderEntry);
        this.on('timeLookupOpened', () => {
            this.MyTimeLookupOpened = true;
        });
        this.on('timeLookupClosed', () => {
            this.MyTimeLookupOpened = false;
        });
        this.observe('alertActionValue', this.showOrderParametrs);
        this.observe('alertImportanceValue', this.setButtonStyle);
        this.observe('OrderEditForPlace', this.orderParamAndText);
        this.observe('messageText', this.ValidationMessageText);
        this.observe('alertTypeValue', () => {
            this.ValidateBTN();
        });
        this.observe('expirationTime', () => {
            this.ValidateBTN();
        });
        this.observe('account', this.onAccountChanged);
        this.observe('instrument', this.onInstrumentChanged, { init: false });
        this.observe('isSymbolInfoOpened', (opened) => {
            if (!opened)
                this.setFocus();
        }, { init: false });
        this.observe('isVisibleOTHTrading', this.updateIsAllowOTHTrading);
        this.getAlertType();
    }
    oncomplete() {
        const _super = Object.create(null, {
            oncomplete: { get: () => super.oncomplete }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.oncomplete.call(this);
            this.completed = false;
            const initAlert = this.get('initAlert');
            if (!isNullOrUndefined(initAlert)) {
                yield this.setUpdateParamsAsync(initAlert);
            }
            else {
                yield this.setDefaultPropertiesAsync();
            }
            this.addScrollToContainer();
            this.completed = true;
            this.ValidateBTN();
        });
    }
    onInstrumentChanged(n, o) {
        this.NeedQuote = false;
        if (!isNullOrUndefined(o)) {
            this.unsubscribeQuotes(o);
        }
        if (!isNullOrUndefined(n)) {
            this.subscribeQuotes(n);
        }
        this.NeedQuote = true;
        this.ValidateBTN();
        this.updateIsVisibleOTHTrading();
    }
    onAccountChanged(account) {
        if (isNullOrUndefined(account)) {
            return;
        }
        this.updateValue();
        this.accountLink_Out();
        this.updateIsVisibleOTHTrading();
    }
    showOrderParametrs(newValue) {
        if (newValue === AlertAction.PLACE_ORDER_NOTIFY) {
            void this.set({ showOrderParametrs: true, visibleInfoBtn: true });
            if (this.get('OrderEditForPlace')) {
                void this.set('enabled', true);
            }
        }
        else {
            void this.set({ showOrderParametrs: false, visibleInfoBtn: false });
        }
        this.ValidateBTN();
    }
    ValidationMessageText(newValue) {
        // let tooltip = Resources.getResource('screen.Alerts.Button.Create.Tooltip')
        // if (!newValue.length)
        //     this.set({ enabledCreateAlert: false, createBtnTooltip: tooltip })
        // else
        //     this.set({ enabledCreateAlert: true, createBtnTooltip: '' })
        this.ValidateBTN();
    }
    orderParamAndText(newValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isNullOrUndefined(newValue)) {
                this.correctingOrderEditParams(newValue);
                let text = yield newValue.getConfirmationText();
                // remove fucking ' ?'
                text = text.replace(' ?', '');
                void this.set({ infoBtnTooltip: text });
            }
            else {
                void this.set({ infoBtnTooltip: '' });
            }
            this.ValidateBTN();
        });
    }
    ValidateBTN() {
        if (!this.completed) {
            return true;
        }
        const btnEnabled = this.getBtnEnability();
        void this.set({
            enabledCreateAlert: btnEnabled,
            createBtnTooltip: this.getBtnToolTip()
        });
        return btnEnabled;
    }
    getBtnEnability() {
        const dataOE = this.get('OrderEditForPlace');
        const dataText = this.get('messageText');
        const alertType = this.get('alertTypeValue');
        const instrument = this.get('instrument');
        const expirationTime = this.get('expirationTime');
        if (this.get('alertActionValue') === AlertAction.PLACE_ORDER_NOTIFY) {
            if (!dataOE || dataText.isEmpty()) {
                return false;
            }
        }
        if (dataText.isEmpty()) {
            return false;
        }
        if (AlertType.VOLUME === alertType && (instrument === null || instrument === void 0 ? void 0 : instrument.isForexSymbol())) {
            return false;
        }
        if (expirationTime <= new Date()) {
            return false;
        }
        return true;
    }
    getBtnToolTip() {
        const dataOE = this.get('OrderEditForPlace');
        const dataText = this.get('messageText');
        const alertType = this.get('alertTypeValue');
        const instrument = this.get('instrument');
        const expirationTime = this.get('expirationTime');
        if (this.get('alertActionValue') === AlertAction.PLACE_ORDER_NOTIFY && !dataOE) {
            return Resources.getResource('alert.specifyOrder.Error');
        }
        if (dataText.isEmpty()) {
            return Resources.getResource('screen.Alerts.Button.Create.Tooltip');
        }
        if (AlertType.VOLUME === alertType && (instrument === null || instrument === void 0 ? void 0 : instrument.isForexSymbol())) {
            return Resources.getResource('alert.specifyAlertType.Error');
        }
        if (expirationTime <= new Date()) {
            return Resources.getResource('alert.specifyExpirationTime.Error');
        }
        return '';
    }
    showOrderEntry(context) {
        const me = this;
        if (me.get('isOEOpened')) {
            return;
        }
        const myData = this.get('OrderEditForPlace');
        const myOrderEntryPanel = MainWindowManager.Factory.addPanel(PanelNames.AdvancedOrderEntry, null, function (panel) {
            panel.removeParentActiveControl(); //to display lookup correctly
            const btn = context.original.currentTarget;
            const btnContainerRect = btn.parentElement.getBoundingClientRect();
            panel.observe('height', function () {
                const h = this.find('*').offsetHeight;
                const newLoc = ControlsUtils.CorrectPopupLocation(new Rectangle(btnContainerRect.x, btnContainerRect.y + btnContainerRect.height, panel.get('width'), h));
                panel.setLocation(newLoc.newX, newLoc.newY);
            });
            panel.set({
                accountVisible: false,
                canLinkByAccount: false,
                isCreateOrderParmeters: true,
                placeButtonLocalizationKey: 'panel.newOrderEntry.AddOrder',
                placeButtonAdditionalClass: 'alertStyle',
                showCustomFullscreenCloud: true
            });
            if (!myData) {
                panel.set({ instrument: me.get('instrument') });
                return;
            }
            const data = me.getOrderEditParams(myData);
            me.correctingOrderEditParams(data);
            panel.set({ instrument: data.instrument }).then(function () {
                // panel.setOrderEdit(data)
                panel.selectOrderType(data.getOrderTypeId());
                panel.setOrderEdit(data);
                const leverage = data.leverageValue;
                panel
                    .set({
                    side: data.side,
                    defaultTif: data.tif,
                    productType: data.productType,
                    isAllowOTHTrading: data.isAllowOTHTrading
                    // defaultQuantity: new Quantity(data.quantity.value, data.quantity.inLots)
                })
                    .then(() => {
                    panel.set('leverageValue', leverage);
                }); // product type needs to be set first
                const OEQty = !isNullOrUndefined(panel.Controls) ? panel.Controls.OEQuantity : null;
                if (!isNullOrUndefined(OEQty)) {
                    const cashMode = myData.isCashQtyMode;
                    const value = cashMode ? data.cash : data.quantity.value;
                    const inLots = cashMode ? OEQty.get('inLots') : data.quantity.inLots;
                    void OEQty.set({ cash: data.cash }).then(() => {
                        void OEQty.set({ isCashMode: cashMode, defaultQuantity: new Quantity(value, inLots) });
                    });
                }
            });
        });
        myOrderEntryPanel.skipOnSave = true;
        void me.set('isOEOpened', true);
        myOrderEntryPanel.close = function () {
            // FOCUS MAGIC
            AdvancedOrderEntry.prototype.close.call(this);
            void me.set('isOEOpened', false);
            me.setFocus();
        };
        myOrderEntryPanel.OnComplete.Subscribe(this.onOrderEntryComplete.bind(this, myOrderEntryPanel), this);
        myOrderEntryPanel.NewParametrsHandler = function (orderEdit) {
            // параметры ОЕ дальше сетим где нужно
            // нам нужен для всех параметров orderEdit
            // я его сюда докинул, из него текст заебашить в i и данные для сохранения, а также этот обьект хранить в алерте OrderEditForPlace
            // там его серелиализуем для хранения на сервере и в обраточку заебашим для плейса при загрузке. изи катка)
            // из него будем ОЕ заполнять при переоткрытии как в assets panel
            // потом просто вызываешь плейс и всё
            // DataCache.FOrderExecutor.placeOrderPromise(orderEdit)
            if (PriceLimitValidation.SendRejectIfNotValidateLimitsByOrderEdit(orderEdit)) {
                // #112368
                return;
            }
            const myEdit = me.getOrderEditParams(orderEdit);
            void me.set({ OrderEditForPlace: myEdit });
        };
    }
    setFocus() {
        super.setFocus();
        if (!isNullOrUndefined(this.afterSetFocus)) {
            this.afterSetFocus();
        }
    }
    onOrderEntryComplete(oe) {
        oe.OnComplete.UnSubscribe(this.onOrderEntryComplete, this);
        const acc = this.get('account');
        if (!isNullOrUndefined(oe) && !isNullOrUndefined(acc)) {
            oe.set('account', acc);
        }
    }
    // GetCopy
    getOrderEditParams(orderEdit) {
        const newEdit = DataCache.OrderParameterContainer.OrderTypes[orderEdit.getOrderTypeId()].createOrderEditObject({
            dataCache: DataCache
        });
        const edit = new OrderEditUpdateData(null, orderEdit.getTradingData());
        newEdit.placedFrom = PlacedFrom.WEB_ALERT;
        newEdit.updateParameters(edit);
        const copiedSLTP = JSON.parse(JSON.stringify(orderEdit.sltp));
        newEdit.sltp.sl = copiedSLTP.sl;
        newEdit.sltp.tp = copiedSLTP.tp;
        newEdit.sltp.trailingStop = copiedSLTP.trailingStop;
        newEdit.limitPrice = orderEdit.limitPrice;
        newEdit.stopPrice = orderEdit.stopPrice;
        newEdit.trailingStop = orderEdit.trailingStop;
        newEdit.AlerEdit = true;
        newEdit.isCashQtyMode = orderEdit.isCashQtyMode; // #124071
        newEdit.isAllowOTHTrading = orderEdit.isAllowOTHTrading;
        return newEdit;
    }
    correctingOrderEditParams(orderEdit) {
        const iSlTpParams = orderEdit.getRawSLTP();
        orderEdit.sltp.updateOffsetTypes();
        orderEdit.sltp.updateAllowedRules(orderEdit.instrument, orderEdit.account);
        orderEdit.setSLTP(iSlTpParams);
    }
    setButtonStyle() {
        const importance = this.get('alertImportanceValue');
        if (importance === AlertImportance.HIGH) {
            void this.set('advancedButtonStyle', 'js-button-create-alert-red');
        }
        if (importance === AlertImportance.MEDIUM) {
            void this.set('advancedButtonStyle', 'js-button-create-alert-yellow');
        }
        if (importance === AlertImportance.LOW) {
            void this.set('advancedButtonStyle', 'js-button-create-alert-green');
        }
    }
    getAlertType() {
        void this.set({ alertTypeValue: AlertType.BID });
    }
    onCancelClick() {
        this.close();
    }
    lostFocus() {
        super.lostFocus();
        // FOCUS MAGIC
        if (this.checkPossibilityClosing()) {
            this.close();
        }
    }
    checkPossibilityClosing() {
        const insDDF = MainWindowManager.TerceraInstrumentLookupDropDownForm;
        const accDDF = MainWindowManager.TerceraAccountLookupDropDownForm;
        return (!this.get('isOEOpened') &&
            !this.MyTimeLookupOpened &&
            !this.get('isSymbolInfoOpened') &&
            !(insDDF === null || insDDF === void 0 ? void 0 : insDDF.get('visible')) &&
            !(accDDF === null || accDDF === void 0 ? void 0 : accDDF.get('visible')));
    }
    onCreateOrApplyClick() {
        const enabled = this.ValidateBTN();
        if (!enabled) {
            return;
        }
        const paramDataToSend = new AlertSendToServerItem();
        paramDataToSend.Account = this.get('account');
        paramDataToSend.Instrument = this.get('instrument');
        paramDataToSend.AlertType = this.get('alertTypeValue');
        paramDataToSend.Condition = this.get('alertConditionValue');
        paramDataToSend.Value = this.get('alertValue');
        paramDataToSend.Importance = this.get('alertImportanceValue');
        paramDataToSend.AfterExecute = this.get('alertExecuteValue');
        paramDataToSend.Action = this.get('alertActionValue');
        paramDataToSend.Notification = this.get('alertNotifyValue');
        paramDataToSend.Expiration = this.get('expirationTime');
        paramDataToSend.MessageText = this.get('messageText');
        paramDataToSend.MessageText = paramDataToSend.MessageText.trim();
        paramDataToSend.IsAllowOTHTrading = this.get('isAllowOTHTrading');
        paramDataToSend.OrderPlaceParametrs = null;
        paramDataToSend.Active = true;
        const okClick = function () {
            if (!isNullOrUndefined(this.myAlert)) {
                paramDataToSend.AlertId = this.myAlert.AlertId;
            }
            DataCache.AlertManager.SendAlertToServer(paramDataToSend);
            this.close();
        }.bind(this);
        const cancelClick = function () {
            void this.set('isOEOpened', false);
            this.setFocus();
        }.bind(this);
        if (paramDataToSend.Action === AlertAction.PLACE_ORDER_NOTIFY) {
            const OrderPlaceParametrs = this.get('OrderEditForPlace');
            paramDataToSend.OrderPlaceParametrs = OrderPlaceParametrs.getDataForRequest();
            void this.set('isOEOpened', true);
            okClick();
        }
        else {
            okClick();
        }
    }
    setUpdateParamsAsync(alert) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isNullOrUndefined(alert)) {
                return Promise.resolve();
            }
            yield this.set('account', alert.Account);
            yield this.set('instrument', alert.Instrument);
            yield this.set('alertNotifyValue', alert.Notification);
            yield this.set('alertTypeValue', alert.AlertType);
            yield this.set('alertConditionValue', alert.Condition);
            yield this.set('alertValue', alert.Value);
            yield this.set('alertImportanceValue', alert.Importance);
            yield this.set('alertExecuteValue', alert.AfterExecute);
            let action = alert.Action;
            const allowPlace = AlertUtils.AllowPlaceOrder();
            if (action === AlertAction.PLACE_ORDER_NOTIFY && !allowPlace) {
                action = AlertAction.NOTIFY;
            }
            yield this.set('alertActionValue', action);
            yield this.set('isAllowOTHTrading', alert.IsAllowOTHTrading);
            yield this.set('messageText', alert.MessageText);
            yield this.set('expirationTime', alert.Expiration);
            yield this.set('isUpdate', true);
            yield this.set('header', Resources.getResource('screen.Alerts.Header.Edit'));
            if (allowPlace) {
                yield this.set({ OrderEditForPlace: alert.OrderPlaceParametrs });
            }
            this.myAlert = alert;
        });
    }
    localize() {
        super.localize();
        const applyBtnText = Resources.getResource('screen.MonthCalendarPopupForm.Apply');
        void this.set({
            buttonCreateText: Resources.getResource('screen.Alerts.Button.Create'),
            buttonApplyText: Resources.getResource('screen.PanelSettingsScreen.applyButton'),
            buttonCancelText: Resources.getResource('general.messageBox.cancel'),
            datePickerButtons: [new DateSelectorButton(applyBtnText, this.setExpirationTime.bind(this))]
        });
    }
    subscribeQuotes(instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        this.newQuote(instrument.LastQuote);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_TRADES);
    }
    unsubscribeQuotes(instrument) {
        if (isNullOrUndefined(instrument)) {
            return;
        }
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_TRADES);
    }
    newQuote(quote) {
        if (!isNullOrUndefined(quote) && quote.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            return;
        }
        if (this.NeedQuote) {
            const instrument = this.get('instrument');
            const value = this.getAlertValue();
            this.NeedQuote = false;
            this.unsubscribeQuotes(instrument);
            if (!isNaN(value) && value !== null) {
                void this.set({ alertValue: value });
            }
        }
    }
    updateValue() {
        const val = this.getAlertValue();
        if (val != null) {
            void this.set('alertValue', val);
        }
    }
    getAlertValue() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        if (instrument == null || account == null) {
            return null;
        }
        const Level1 = instrument.Level1;
        const alertTypeValue = this.get('alertTypeValue');
        let value = 0;
        switch (alertTypeValue) {
            case AlertType.BID:
                value = Level1.GetBid(account);
                break;
            case AlertType.ASK:
                value = Level1.GetAsk(account);
                break;
            case AlertType.LAST:
                value = Level1.GetLastPrice(account);
                break;
            case AlertType.CHANGE:
                value = Level1.GetChangePercent(account);
                break;
            case AlertType.VOLUME:
                value = Level1.GetVolume();
                break;
        }
        return value;
    }
    accountLink_In() {
        return false;
    }
    accountLink_Out() { }
    setExpirationTime(newExpirationTime) {
        void this.set({ expirationTime: newExpirationTime });
    }
    setDefaultPropertiesAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set('account', AccountWidgetAccountsController.Account);
            yield this.set('instrument', DataCache.getDefaultInstrument(false));
            yield this.set({
                alertNotifyValue: WDSettings.alertNotification,
                alertTypeValue: WDSettings.alertType,
                alertConditionValue: WDSettings.alertCondition,
                alertImportanceValue: WDSettings.alertImportance,
                alertExecuteValue: WDSettings.alertAfterExecute
            });
        });
    }
    updateIsVisibleOTHTrading() {
        const account = this.get('account');
        const instrument = this.get('instrument');
        const isVisible = !isNullOrUndefined(account) && !isNullOrUndefined(instrument) && account.isAllowOTHTrading(instrument);
        this.set('isVisibleOTHTrading', isVisible);
    }
    updateIsAllowOTHTrading() {
        const isVisible = this.get('isVisibleOTHTrading');
        const isAllowOTHTrading = this.get('isAllowOTHTrading');
        if (isVisible) {
            if (isNullOrUndefined(isAllowOTHTrading)) {
                this.set('isAllowOTHTrading', BaseSettings.isAllowOTHTrading);
            }
        }
        else {
            this.set('isAllowOTHTrading', null);
        }
    }
}
ApplicationPanel.extendWith(CreateAlertPanel, {
    data: function () {
        return {
            width: 400,
            showFooter: true,
            resizable: false,
            dockablePanel: false,
            attached: false,
            showHeader: true,
            account: null,
            instrument: null,
            isUpdate: false,
            movable: false,
            showOrderParametrs: false,
            visibleInfoBtn: false,
            OrderEditForPlace: null,
            enabledCreateAlert: true,
            infoBtnTooltip: '',
            createBtnTooltip: '',
            expirationTime: new Date(),
            isAlertActionSelectorVisible: true,
            isSymbolInfoOpened: false,
            canFilterByAccount: false,
            canLinkByAccount: false,
            initAlert: null,
            style_addition_header: 'attach-detach-panel-header',
            isOEOpened: false,
            isVisibleOTHTrading: false,
            isAllowOTHTrading: null,
            overnightExecutionTextKey: 'screen.Alerts.OvernightProcessing',
            overnightExecutionTooltipKeyActive: 'screen.Alerts.OvernightProcessing.Info.Active',
            overnightExecutionTooltipKeyInActive: 'screen.Alerts.OvernightProcessing.Info.InActive',
            isAllowOTHTradingEvents: {
                onActiveChanged: (isActive) => {
                    this.set('isAllowOTHTrading', isActive);
                }
            }
        };
    },
    partials: {
        bodyPartial: CreateAlertTemplate,
        footerPartial: CreateAlertFooterTemplate
    }
});
